import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';


import {BasicPage} from 'src/components/basic-page';
import {ErrorFlatButton} from 'src/components/error-flat-button';
import {DateInput, TimeInput} from 'src/components/input';
import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {
    CustomValidatedControl,
    ValidatedControl,
    ValidationContext,
    ValidationContextProvider,
} from 'src/contexts/validation-context';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';

import Button, {FlatButton} from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import {FieldDescription} from '@pb/kit-component-field-description';
import {Group, Item} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {Info} from '@pb/kit-component-info';
import Label from '@pb/kit-component-label';

import {
    AVAILABLE_ON_CONTROL,
    EXCEPT_DAY_CONTROL,
    EXCEPT_DAY_DUPLICATE_ERROR,
    EXCEPT_DAY_DUPLICATE_MESSAGE,
    SECTION_NAME,
} from './constants';
import {LiveAgentConfiguratorSkeleton} from './live-agent-configurator.skeleton';
import {useExceptDays} from './useExceptDays';
import {useLiveAgentSettings} from './useLiveAgentSettings';

import type {AccessRight} from 'src/hooks/access-rights/constants';

const LiveAgentConfigurator = () => {
    const {addError} = useContext(ValidationContext);
    const [busy, setBusy] = useState(false);
    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);

    const {
        proceed,
        setIsChunkChanged,
        isCurrentChunkChanged,
    } = useConfirmationChunk({chunkName: 'live-agent', onProceed: () => {
        setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
    }});

    const {
        isLoading: isLiveAgentSettingsLoading,
        loadingError: liveAgentSettingsLoadingError,
        data: liveAgentSettings,
        setData: setLiveAgentSettings,
        isChanged: isLiveAgentSettingsChanged,
        saveChanges: saveLiveAgentSettings,
        discardChanges: discardLiveAgentSettings,
    } = useLiveAgentSettings();

    const {
        isLoading: isExceptDaysLoading,
        loadingError: exceptDaysLoadingError,
        data: exceptDays,
        setData: setExceptDays,
        isChanged: isExceptDaysChanged,
        saveChanges: saveExceptDays,
        discardChanges: discardExceptDays,
    } = useExceptDays();

    useEffect(() => {
        setIsChunkChanged(isLiveAgentSettingsChanged || isExceptDaysChanged);
    }, [isLiveAgentSettingsChanged, isExceptDaysChanged, setIsChunkChanged]);

    const saveChanges = useCallback(async () => {
        const calls = [];

        setBusy(true);
        if (isLiveAgentSettingsChanged) {
            calls.push(await saveLiveAgentSettings());
        }
        if (isExceptDaysChanged) {
            calls.push(await saveExceptDays());
        }
        setBusy(false);

        const errors = [...new Set(calls.filter(Boolean))];
        if (errors.length) {
            setErrorMessage(errors.join('\n'));
        } else {
            proceed();
        }
    }, [
        proceed,
        isLiveAgentSettingsChanged,
        isExceptDaysChanged,
        saveLiveAgentSettings,
        saveExceptDays,
        setErrorMessage,
    ]);

    const discardChanges = useCallback(() => {
        discardLiveAgentSettings();
        discardExceptDays();
        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
    }, [discardLiveAgentSettings, discardExceptDays, setSuccessMessage]);

    const isLoading = useMemo(() => (
        isLiveAgentSettingsLoading || isExceptDaysLoading
    ), [isLiveAgentSettingsLoading, isExceptDaysLoading]);

    const loadingError = useMemo(() => (
        liveAgentSettingsLoadingError || exceptDaysLoadingError
    ), [liveAgentSettingsLoadingError, exceptDaysLoadingError]);

    const updateDateAvailability = useCallback((day: string, value: boolean) => {
        setLiveAgentSettings(prev => prev.map(setting => (setting.weekDay === day ? {
            ...setting,
            isActive: value,
        } : setting)));
    }, [setLiveAgentSettings]);

    const updateLiveAgentStartTime = useCallback((day: string, startTime: string) => {
        setLiveAgentSettings(prev => prev.map(setting => (setting.weekDay === day ? {
            ...setting,
            startTime,
        } : setting)));
    }, [setLiveAgentSettings]);

    const updateLiveAgentEndTime = useCallback((day: string, endTime: string) => {
        setLiveAgentSettings(prev => prev.map(setting => (setting.weekDay === day ? {
            ...setting,
            endTime,
        } : setting)));
    }, [setLiveAgentSettings]);

    const addExceptDay = useCallback(() => {
        setExceptDays(prev => [...prev, '']);
    }, [setExceptDays]);

    const removeExceptDay = useCallback((index: number) => {
        setExceptDays(prev => prev.filter((_, i) => i !== index));
    }, [setExceptDays]);

    const changeExceptDay = useCallback((index: number, value: string) => {
        const updatedDays = exceptDays.map((day, i) => (i === index ? value : day));

        if (value && exceptDays.includes(value)) {
            addError({
                controlId: `${EXCEPT_DAY_CONTROL}-${index}`,
                code: EXCEPT_DAY_DUPLICATE_ERROR,
                message: EXCEPT_DAY_DUPLICATE_MESSAGE,
            });
        }
        setExceptDays(updatedDays);
    }, [exceptDays, addError, setExceptDays]);

    return (
        <Skeleton shown={isLoading} view={<LiveAgentConfiguratorSkeleton/>}>
            <ServerResponseHandler
                serverError={loadingError}
                accessRight={ACCESS_RIGHTS.LIVE_AGENT_CONFIGURATOR as AccessRight}
                sectionName={SECTION_NAME}>
                <BasicPage
                    title={SECTION_NAME}
                    buttonsBlock={(
                        <Group>
                            <Button
                                disabled={!isCurrentChunkChanged || busy}
                                variant="Type.SECONDARY"
                                onClick={discardChanges}>
                                Discard Changes
                            </Button>
                            <Button
                                busy={busy}
                                disabled={!isCurrentChunkChanged}
                                variant="Type.PRIMARY"
                                onClick={saveChanges}>
                                Save Changes
                            </Button>
                        </Group>
                    )}>
                    <Item className="group-item pd-l">
                        <Group direction="v">
                            <Item>
                                <Label>Available on</Label>
                                <ResponsiveElementGroup>
                                    {liveAgentSettings.map(({weekDay, startTime, endTime, isActive}) => (
                                        <Group key={weekDay} direction="v">
                                            <CheckBox
                                                label={
                                                    weekDay.slice(0, 1).toUpperCase() +
                                                    weekDay.slice(1).toLowerCase()
                                                }
                                                checked={isActive}
                                                onCheckBoxChange={value => updateDateAvailability(
                                                    weekDay, value.event.currentTarget.checked,
                                                )}
                                            />
                                            <Item>
                                                <Label>From</Label>
                                                <TimeInput
                                                    time={startTime}
                                                    onBlur={time => updateLiveAgentStartTime(weekDay, time)}
                                                    disabled={!isActive}
                                                />
                                            </Item>
                                            <Item>
                                                <Label>To</Label>
                                                <CustomValidatedControl name={`${AVAILABLE_ON_CONTROL}-${weekDay}`}>
                                                    {error => (
                                                        <>
                                                            <TimeInput
                                                                time={endTime}
                                                                onBlur={time => updateLiveAgentEndTime(
                                                                    weekDay,
                                                                    time,
                                                                )}
                                                                disabled={!isActive}
                                                            />
                                                            {error ? (
                                                                <WidthItem width={140}>
                                                                    <FieldDescription color="Types.ERROR">
                                                                        {error}
                                                                    </FieldDescription>
                                                                </WidthItem>
                                                            ) : null}
                                                        </>
                                                    )}
                                                </CustomValidatedControl>
                                            </Item>
                                        </Group>
                                    ))}
                                </ResponsiveElementGroup>
                            </Item>
                            <Label>
                                <b>All Times in Seattle (Pacific) Time Zone</b>
                            </Label>
                            <Item>
                                <Label>
                                    <Info type={{popover: 'Expired dates will be deleted automatically'}}>
                                        Except Days
                                    </Info>
                                </Label>
                                <Group direction="v" alignItems="start">
                                    {exceptDays.map((day, index) => (
                                        <ValidatedControl key={index} name={`${EXCEPT_DAY_CONTROL}-${index}`}>
                                            {() => (
                                                <Group gap="xl" alignItems="start">
                                                    <DateInput
                                                        id="except-day-picker"
                                                        width={200}
                                                        selectedDate={day}
                                                        clearable={true}
                                                        onChange={date => changeExceptDay(index, date)}
                                                    />
                                                    {exceptDays.length > 1 && (
                                                        <ErrorFlatButton
                                                            icon={Icons.TIMES_CIRCLE}
                                                            label="Remove"
                                                            onClick={() => removeExceptDay(index)}
                                                        />
                                                    )}
                                                </Group>
                                            )}
                                        </ValidatedControl>
                                    ))}
                                    <FlatButton
                                        disabled={exceptDays.includes('') || exceptDays.length >= 15}
                                        startIcon={<Icon type={Icons.PLUS_CIRCLE}/>}
                                        onClick={addExceptDay}>
                                        <b>Add Day</b>
                                    </FlatButton>
                                </Group>
                            </Item>
                        </Group>
                    </Item>
                </BasicPage>
            </ServerResponseHandler>
        </Skeleton>
    );
};


export const ValidateLiveAgentConfiguration = () => (
    <ValidationContextProvider>
        <LiveAgentConfigurator/>
    </ValidationContextProvider>
);
